/**
 * Lernzeit (Roadmap F-22, Stufe 7): Dauer, Wochendiagramm, Summen je Fach.
 *
 * Eine Sitzung zaehlt zum lokalen Tag ihres Beginns - wer um 23:50 anfaengt,
 * hat an diesem Abend gelernt, nicht am naechsten Morgen.
 */
import { addDays, differenceInDays, isoWeek, startOfWeek, toLocalDate } from '@/lib/datetime';
import type { ID, LocalDate, StudySession } from '@/domain/types';

/** Minuten einer Sitzung; laufende bis `jetzt`. Nie negativ. */
export function minutenVon(s: StudySession, jetzt: Date): number {
  const ende = s.end ? new Date(s.end) : jetzt;
  return Math.max(0, (ende.getTime() - new Date(s.start).getTime()) / 60000);
}

export function tagVon(s: StudySession): LocalDate {
  return toLocalDate(new Date(s.start));
}

/** Mo-So der Woche von `heute`, je Tag die Minuten je Fach. */
export function wochenLernzeit(
  sitzungen: StudySession[], heute: LocalDate, jetzt: Date,
): { date: LocalDate; proFach: Map<ID, number>; summe: number }[] {
  const montag = startOfWeek(heute);
  return Array.from({ length: 7 }, (_, i) => {
    const date = addDays(montag, i);
    const proFach = new Map<ID, number>();
    for (const s of sitzungen) {
      if (tagVon(s) !== date) continue;
      proFach.set(s.subjectId, (proFach.get(s.subjectId) ?? 0) + minutenVon(s, jetzt));
    }
    return { date, proFach, summe: [...proFach.values()].reduce((a, b) => a + b, 0) };
  });
}

/** Summe je Fach, wahlweise nur ab einem Tag. */
export function lernzeitProFach(sitzungen: StudySession[], jetzt: Date, ab?: LocalDate): Map<ID, number> {
  const map = new Map<ID, number>();
  for (const s of sitzungen) {
    if (ab && tagVon(s) < ab) continue;
    map.set(s.subjectId, (map.get(s.subjectId) ?? 0) + minutenVon(s, jetzt));
  }
  return map;
}

/**
 * Lernzeit je Kalenderwoche (F-02 fuer F-22): `wochen` Wochen, die aelteste
 * zuerst, die laufende zuletzt - fuer den Verlauf.
 */
export function lernzeitJeWoche(sitzungen: StudySession[], heute: LocalDate, wochen: number, jetzt: Date): { start: LocalDate; kw: number; minuten: number }[] {
  const erste = addDays(startOfWeek(heute), -7 * (wochen - 1));
  const werte = Array.from({ length: wochen }, (_, i) => {
    const start = addDays(erste, 7 * i);
    return { start, kw: isoWeek(start), minuten: 0 };
  });
  for (const s of sitzungen) {
    const tag = tagVon(s);
    if (tag < erste || tag > heute) continue;
    // In Kalendertagen, nicht in Millisekunden - sonst kippt eine Woche an der Zeitumstellung.
    const i = Math.floor(differenceInDays(erste, tag) / 7);
    werte[i]!.minuten += minutenVon(s, jetzt);
  }
  return werte;
}

/** "1 h 25 min", "40 min", "0 min". */
export function dauerText(minuten: number): string {
  const m = Math.floor(minuten);
  const h = Math.floor(m / 60);
  return h > 0 ? `${h} h ${m % 60} min` : `${m} min`;
}

/** Stoppuhr-Anzeige "0:12:05". */
export function uhrText(minuten: number): string {
  const sek = Math.floor(minuten * 60);
  const h = Math.floor(sek / 3600);
  const m = Math.floor((sek % 3600) / 60);
  return `${h}:${String(m).padStart(2, '0')}:${String(sek % 60).padStart(2, '0')}`;
}
